'use client';

/**
 * `useJourneyHints`, publishes piano-roll hints based on the current
 * journey snapshot.
 *
 * Pure side effect: takes the snapshot, derives a `HintNote[]` via
 * `computeJourneyHints`, and feeds it through `useHintPublisher` under
 * the `'journey'` source slot. `useHintPublisher` handles the cleanup , 
 * unmounting the consumer retracts the slot.
 *
 * Memoization is on the *content* of the hint (label + serialized midis)
 * so identity-stable inputs don't trigger re-publishes. The publish/retract
 * dance inside `useHintPublisher` is keyed on the memoized object.
 */

import { useMemo, useRef } from 'react';
import type { Hint } from '@/contexts/hint-context';
import { useHintPublisher } from '@/hooks/use-hint-publisher';
import type { JourneySnapshot } from '@/lib/learning-journey/journey';
import { computeJourneyHints } from './journey-hint-mapping';

const HINT_SOURCE = 'journey';

export function useJourneyHints(snapshot: JourneySnapshot | null): void {
  // We re-derive the hint on every snapshot change, the journey only
  // notifies subscribers on actual state mutations (capture writes are
  // silent), so the cost is bounded. To avoid republishing when the set
  // of hint notes is unchanged (e.g. unrelated state field flipped), we
  // memoize against the canonical signature of the computed midis.
  const lastSignatureRef = useRef<string | null>(null);
  const lastHintRef = useRef<Hint | null>(null);

  const hint = useMemo<Hint | null>(() => {
    const midis = computeJourneyHints(snapshot);

    if (midis.length === 0) {
      lastSignatureRef.current = '';
      lastHintRef.current = null;

      return null;
    }

    const signature = midis.map((n) => `${n.midi}:${n.color}`).join(',');

    if (signature === lastSignatureRef.current && lastHintRef.current !== null) {
      return lastHintRef.current;
    }

    // Mirror the MIDIs into `voicing` so the `VirtuosaLayoutHelper` FAB
    // pill places the same keys on the AZERTY layout. The piano-roll
    // consumer keeps reading `midis` (hand-colored); the pill reads
    // `voicing` (raw MIDIs). Same hint, two surfaces.
    const voicing = midis.map((n) => n.midi);

    const next: Hint = { source: HINT_SOURCE, midis, voicing };
    lastSignatureRef.current = signature;
    lastHintRef.current = next;

    return next;
  }, [snapshot]);

  useHintPublisher(hint);
}
